import React, { useState, useEffect } from "react";
import { ArrowLeft, Eye, EyeOff, RotateCcw, ArrowRight, Monitor } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import {QRCodeSVG} from 'qrcode.react';
import ErrorModal from '../ui/ErrorModal';

function GuestDeviceSetup() {
    const [familyName, setFamilyName] = useState("");
    const [familyUserID, setFamilyUserID] = useState("");
    const [passcode, setPasscode] = useState("");
    const [wifiSSID, setWifiSSID] = useState("");
    const [wifiPassword, setWifiPassword] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [qrGenerated, setQrGenerated] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isVerified, setIsVerified] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const encodedData = urlParams.get('data');

        if (!encodedData) {
            setErrorMessage("The link you've entered is either incorrect or incomplete. Please check the link you received or ask your contact to send you a new link.");
            setShowErrorModal(true);
            return;
        }

        try {
            // Decode the base-64 encoded parameters
            const decodedData = JSON.parse(atob(encodedData));
            const { token, fID: familyID, fName: familyName, ts: timestamp, pad } = decodedData;

            if (!token || !familyID || !familyName || !timestamp || !pad) {
                setErrorMessage("The link you've entered is either incorrect or incomplete. Please check the link you received or ask your contact to send you a new link.");
                setShowErrorModal(true);
                return;
            }

            const currentTime = Math.floor(Date.now() / 1000);
            const oneWeekS = 7 * 24 * 60 * 60;
            if (currentTime - parseInt(timestamp) > oneWeekS) {
                setErrorMessage("The link is only valid for 1 week and has expired. Please ask the account holder to send you a new link.");
                setShowErrorModal(true);
                return;
            }

            setFamilyName(familyName);
            setFamilyUserID(familyID + "@family.higlo.ai");
            setPasscode(token);
            setIsVerified(true);
        } catch (error) {
            console.error("Error decoding link data:", error);
            setErrorMessage("The link you've entered is invalid or corrupted. Please check the link you received or ask your contact to send you a new link.");
            setShowErrorModal(true);
        }
    }, []);

    const handleStep1Next = () => {
        setCurrentStep(2);
    };

    const handleStep2Back = () => {
        setCurrentStep(1);
    };

    const handleStep2Next = async () => {
        // Validate WiFi credentials before proceeding
        if (!wifiSSID) {
            setErrorMessage("WiFi SSID cannot be empty.");
            setShowErrorModal(true);
            return;
        }
        if (wifiSSID.includes(';')) {
            setErrorMessage("Your WiFi network name contains a semicolon (;) which is not supported. Please rename your WiFi network or use a different network.");
            setShowErrorModal(true);
            return;
        }
        setCurrentStep(3);
        // Automatically generate QR code when moving to step 3
        setQrGenerated(true);
        setErrorMessage(""); // Clear message on successful submission
        setShowErrorModal(false);
        
        // Scroll to the main card after QR code is generated
        setTimeout(() => {
            const mainCard = document.querySelector('.w-full');
            if (mainCard) {
                mainCard.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }
        }, 100);
    };

    const handleSkipWiFi = () => {
        setWifiSSID("");
        setWifiPassword("");
        setCurrentStep(3);
        setQrGenerated(true);
        setErrorMessage("");
        setShowErrorModal(false);
    };

    const handleStep3Back = () => {
        setCurrentStep(2);
        setQrGenerated(false);
    };

    const handleRegenerateQR = () => {
        setQrGenerated(false);
        setWifiSSID("");
        setWifiPassword("");
        setCurrentStep(1);
    };

    const renderStep1 = () => (
        <div className="max-w-md mx-auto mt-6 space-y-6">
            <div className="text-center">
                <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                        <Monitor className="h-6 w-6 text-gray-700" />
                    </div>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Connect Hearth to TV</h3>
                <div className="max-w-xs mx-auto">
                    <ol className="text-left space-y-2 text-slate-600">
                        <li>1. Connect the Hearth to your TV.</li>
                        <li>2. Power it up and Open the Lid.</li>
                        <li>3. Hit Next once you see the 'Setup Device' page on the TV.</li>
                    </ol>
                </div>
            </div>
            
            <div className="flex justify-center pt-4">
                <Button
                    variant="default"
                    onClick={handleStep1Next}
                    className="w-32 bg-gray-800 hover:bg-gray-700 flex items-center gap-2"
                >
                    Next
                    <ArrowRight className="h-4 w-4" />
                </Button>
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div className="max-w-md mx-auto mt-6 space-y-6">
            <div className="text-center">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">WiFi Setup</h3>
                <p className="text-slate-600">Enter your WiFi SSID and Password to connect</p>
            </div>
            
            <div className="space-y-4">
                <Input 
                    type="text" 
                    placeholder="WiFi SSID" 
                    value={wifiSSID} 
                    onChange={(e) => setWifiSSID(e.target.value)} 
                />
                
                <div className="relative">
                    <Input 
                        type={showPassword ? "text" : "password"} 
                        placeholder="WiFi Password" 
                        value={wifiPassword} 
                        onChange={(e) => setWifiPassword(e.target.value)} 
                        className="pr-10"
                    />
                    <Button 
                        variant="ghost"
                        size="icon"
                        type="button" 
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6"
                    >
                        {showPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </Button>
                </div>
            </div>
            
            <div className="flex gap-3 pt-4">
                <Button
                    variant="outline"
                    onClick={handleStep2Back}
                    className="w-full"
                >
                    Back
                </Button>
                <Button
                    variant="default"
                    onClick={handleStep2Next}
                    className="w-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center gap-2"
                >
                    Next
                    <ArrowRight className="h-4 w-4" />
                </Button>
            </div>

            <div className="text-center pt-2">
                <button
                    onClick={handleSkipWiFi}
                    className="text-sm text-slate-500 hover:text-slate-700 underline"
                >
                    Hearth is already connected to WiFi
                </button>
            </div>
        </div>
    );

    const renderStep3 = () => (
        <div className="max-w-2xl mx-auto mt-6 space-y-6">
            <div className="text-center">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">QR Code</h3>
                <p className="text-slate-600">
                    {wifiSSID
                        ? "Show the QR code below to the Hearth to connect."
                        : "Show the QR code below to the Hearth to sign in. WiFi setup will be skipped."
                    }
                </p>
            </div>
            
            <div className="flex justify-center">
                <div className="max-w-full overflow-hidden">
                    <QRCodeSVG
                        value={[`${wifiSSID};`, `${wifiPassword};`, `${familyUserID};`, `${passcode};`]}
                        size={280}
                        className="w-full h-auto max-w-70"
                        id="qr-code-element"
                    />
                </div>
            </div>

            <div className="flex justify-center">
                <Button
                    variant="outline"
                    onClick={handleStep3Back}
                    className="flex items-center justify-center gap-2"
                >
                    Back
                </Button>
            </div>
        </div>
    );

    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return renderStep1();
            case 2:
                return renderStep2();
            case 3:
                return renderStep3();
            default:
                return renderStep1();
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
            <div className="max-w-4xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="min-w-0 flex-1">
                            <h1 className="text-2xl font-bold text-slate-900">Setup Hearth</h1>
                            <p className="text-slate-600">Sign in the Hearth device and connect it to WiFi</p>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <Card className="w-full">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-2xl">
                            <h2 className="text-lg font-semibold text-slate-900">{familyName}</h2>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Progress indicator */}
                        {isVerified && (
                            <div className="flex justify-center mb-6">
                                <div className="flex items-center space-x-2">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                        currentStep >= 1 ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-600'
                                    }`}>
                                        1
                                    </div>
                                    <div className={`w-8 h-1 ${currentStep >= 2 ? 'bg-gray-600' : 'bg-gray-200'}`}></div>
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                        currentStep >= 2 ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-600'
                                    }`}>
                                        2
                                    </div>
                                    <div className={`w-8 h-1 ${currentStep >= 3 ? 'bg-gray-600' : 'bg-gray-200'}`}></div>
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                        currentStep >= 3 ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-600'
                                    }`}>
                                        3
                                    </div>
                                </div>
                            </div>
                        )}

                        {isVerified && renderStepContent()}

                        {/* Error Modal */}
                        <ErrorModal
                            isOpen={showErrorModal}
                            message={errorMessage}
                            onClose={() => {
                                setShowErrorModal(false);
                                setErrorMessage("");
                            }}
                        />
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

export default GuestDeviceSetup;
